import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { getDefaultRouteByPermissions } from './getDefaultRouteByPermissions';
import { getStoredPermissions, removeStoredPermissions, setStoredPermissions } from './permissionsStorage';

const TOKEN_COOKIE_KEY = 'Token';
const ROLE_COOKIE_KEY = 'Role';
const VALIDATE_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/user/validate-token`;
const AUTH_REFRESH_SKIP_PATTERNS = ['/student/login', '/student/one-time-password-chnage', '/student/one-time-user-password-chnage'];

const getTokenExpiry = () => {
  const token = Cookies.get(TOKEN_COOKIE_KEY);
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return null;
    return new Date(decoded.exp * 1000);
  } catch (error) {
    console.error('Failed to decode token expiry:', error);
    return null;
  }
};

const setCookie = (key, value) => {
  const expires = getTokenExpiry();
  const options = expires ? { expires } : undefined;
  Cookies.set(key, value, options);
};

const getRole = () => String(Cookies.get(ROLE_COOKIE_KEY) || '').trim().toLowerCase();

const normalizePermissions = (permissions) => {
  if (!Array.isArray(permissions)) return [];
  return permissions
    .map((perm) => ({
      module: perm?.module || '',
      actions: perm?.actions || {}
    }))
    .sort((a, b) => a.module.localeCompare(b.module));
};

const arePermissionsEqual = (left, right) => {
  const leftStr = JSON.stringify(normalizePermissions(left));
  const rightStr = JSON.stringify(normalizePermissions(right));
  return leftStr === rightStr;
};

const hasAnyPermission = (permissions) =>
  Array.isArray(permissions) &&
  permissions.some((perm) =>
    Object.values(perm?.actions || {}).some((allowed) => allowed === true)
  );

// Dummy response parsing: adjust keys to match your API response.
const extractPermissions = (data) => {
  if (!data || typeof data !== 'object') return [];
  return data?.data?.permissions || data?.permissions || [];
};

const extractToken = (data) => {
  if (!data || typeof data !== 'object') return null;
  return data?.data?.token || data?.token || data?.newToken || null;
};

const isAbsoluteUrl = (url = '') => /^https?:\/\//i.test(url);

const buildUrl = (baseURL, url) => {
  if (!baseURL || !url || isAbsoluteUrl(url)) return url;
  const trimmedBase = baseURL.replace(/\/+$/, '');
  const trimmedUrl = url.replace(/^\/+/, '');
  return `${trimmedBase}/${trimmedUrl}`;
};

const emitAuthUpdated = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('auth:updated'));
};

const shouldSkipRefresh = (requestUrl) => {
  if (!requestUrl) return true;
  return requestUrl.includes(VALIDATE_ENDPOINT) || AUTH_REFRESH_SKIP_PATTERNS.some((pattern) => requestUrl.includes(pattern));
};

export const initRequestInterceptors = () => {
  if (typeof window === 'undefined') return;
  if (window.__permissionsInterceptorInstalled) return;

  const rawFetch = window.fetch.bind(window);
  const clearAuthCookies = () => {
    const removeOpts = { path: '/' };
    Cookies.remove(TOKEN_COOKIE_KEY, removeOpts);
    Cookies.remove('Role', removeOpts);
    Cookies.remove('role', removeOpts);
    Cookies.remove('user', removeOpts);
    Cookies.remove('_Id', removeOpts);
    Cookies.remove('isPasswordChanged', removeOpts);
    removeStoredPermissions();

    // Fallback for cookies set without explicit path
    Cookies.remove(TOKEN_COOKIE_KEY);
    Cookies.remove('Role');
    Cookies.remove('role');
    Cookies.remove('user');
    Cookies.remove('_Id');
    Cookies.remove('isPasswordChanged');
  };

  const handleUnauthorized = () => {
    clearAuthCookies();
    if (window.handleUnauthorized) {
      window.handleUnauthorized();
    }
  };

const refreshPermissions = async (requestUrl) => {
    if (shouldSkipRefresh(requestUrl)) return;

    const token = Cookies.get(TOKEN_COOKIE_KEY);
    if (!token) return;
    const headers = { 'Content-Type': 'application/json' };

    try {
      const response = await rawFetch(VALIDATE_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token })
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) return;

      const data = await response.json();
      // If logout/clear happened while refresh request was in-flight, do not restore cookies.
      if (!Cookies.get(TOKEN_COOKIE_KEY)) return;

      const nextPermissions = extractPermissions(data);
      const nextToken = extractToken(data);

      const currentRole = getRole();
      let effectiveRole = currentRole;
      if (!effectiveRole) {
        try {
          const decodedRole = jwtDecode(nextToken || token)?.role;
          effectiveRole = String(decodedRole || '').trim().toLowerCase();
        } catch (error) {
          console.error('Failed to decode role from token:', error);
        }
      }
      const isStudent = effectiveRole === 'student';
      let didUpdateAuth = false;

      if (!currentRole && effectiveRole) {
        setCookie(ROLE_COOKIE_KEY, String(effectiveRole));
        didUpdateAuth = true;
      }

      if (!isStudent && !hasAnyPermission(nextPermissions)) {
        handleUnauthorized();
        return;
      }

      const currentPermissions = getStoredPermissions();

      if (!isStudent && !arePermissionsEqual(currentPermissions, nextPermissions)) {
        setStoredPermissions(nextPermissions, getTokenExpiry() ? { expires: getTokenExpiry() } : undefined);
        didUpdateAuth = true;
        const defaultRoute = getDefaultRouteByPermissions();
        if (defaultRoute && window.location.pathname !== defaultRoute) {
          window.location.replace(defaultRoute);
          return;
        }
      }

      if (nextToken && nextToken !== Cookies.get(TOKEN_COOKIE_KEY)) {
        setCookie(TOKEN_COOKIE_KEY, nextToken);
        didUpdateAuth = true;
        try {
          const decoded = jwtDecode(nextToken);
          const nextRole = decoded?.role;
          if (nextRole) {
            setCookie(ROLE_COOKIE_KEY, String(nextRole));
            didUpdateAuth = true;
          }
        } catch (error) {
          console.error('Failed to decode role from token:', error);
        }
      }

      if (didUpdateAuth) {
        emitAuthUpdated();
      }
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    }
  };

  axios.interceptors.request.use((config) => {
    const requestUrl = buildUrl(config.baseURL, config.url);
    refreshPermissions(requestUrl);
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (response?.status === 401) {
        handleUnauthorized();
      }
      return response;
    },
    (error) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
      return Promise.reject(error);
    }
  );

  window.fetch = (input, init) => {
    const requestUrl = typeof input === 'string' ? input : input?.url;
    refreshPermissions(requestUrl);
    return rawFetch(input, init).then((response) => {
      if (response?.status === 401) {
        handleUnauthorized();
      }
      return response;
    });
  };

  window.__permissionsInterceptorInstalled = true;
};
